import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useQuery } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiResponse } from "../api/apiResponse";
import { getCurrentUserInfo } from "../api/endpoints/auth";
import { clearAuthCredentials } from "../api/helpers/authHelpers";
import CandidateModel from "../api/models/CandidateModel";
import LiaisonModel from "../api/models/LiaisonModel";
import StudentModel from "../api/models/StudentModel";
import TutorModel from "../api/models/TutorModel";
import SupervisorModel from "../api/models/SupervisorModel";
import UserModel from "../api/models/UserModel";

export enum UserType {
  Liaison = "liaison",
  Student = "student",
  Candidate = "candidate",
  Tutor = "tutor",
  Supervisor = "supervisor",
}

export type UserModelType =
  | LiaisonModel
  | StudentModel
  | CandidateModel
  | SupervisorModel
  | TutorModel;

export interface AuthContextInterface {
  currentUser: UserModelType | null;
  userType: UserType | null;
  fetchCurrentUser: () => Promise<void>;
  logout: () => void;
  isLoadingUser: boolean;
}

export const AuthContext = createContext<AuthContextInterface | null>(null);

const setCurrentUserData = (userResponseData: any) => {
  let user = null;
  let userType = null;
  if (!!userResponseData.liaison) {
    userType = UserType.Liaison;
    user = new LiaisonModel({
      ...userResponseData.liaison,
      account: userResponseData,
    });
  } else if (!!userResponseData.student) {
    userType = UserType.Student;
    user = new StudentModel({
      ...userResponseData.student,
      account: userResponseData,
    });
  } else if (!!userResponseData.candidate) {
    userType = UserType.Candidate;
    user = new CandidateModel({
      ...userResponseData.candidate,
      account: userResponseData,
    });
  } else if (!!userResponseData.supervisor) {
    userType = UserType.Supervisor;
    user = new SupervisorModel({
      ...userResponseData.supervisor,
      account: userResponseData,
    });
  } else if (!!userResponseData.tutor) {
    userType = UserType.Tutor;
    user = new TutorModel({
      ...userResponseData.tutor,
      account: userResponseData,
    });
  }
  return {
    userType,
    data: user,
  };
};

export function AuthContextProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    data: currentUser,
    isLoading: isLoadingUser,
    refetch,
  } = useQuery("currentUser", getCurrentUserInfo, {
    select: (response: ApiResponse) => {
      if (response.isSuccess) return setCurrentUserData(response.data);
    },
    staleTime: Infinity,
  });

  const logout = () => {
    clearAuthCredentials();
    refetch();
    navigate("/login");
  };

  const fetchCurrentUser = async () => {
    /**
     *  While refactoring this auth context, I found some consumers of this context need to fetch the current user data manually
     *  And expect the the type of this function to be a Promise<void>
     *  That's why I'm using this async function here and not using the refetch function directly to avoid breaking the existing code
     */
    await refetch();
  };

  const authContext = {
    currentUser: currentUser?.data || null,
    userType: currentUser?.userType || null,
    fetchCurrentUser,
    isLoadingUser,
    logout,
  };

  return (
    <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  return context;
}

export function useCurrentUser(): UserModel | null {
  const context: AuthContextInterface | null = useContext(AuthContext);

  if (!context) return null;
  return context.currentUser;
}

export type CurrentActor = {
  ActorId: number;
  ActorType: UserType;
};

export function useCurrentActor(): CurrentActor {
  const context: AuthContextInterface | null = useContext(AuthContext);

  if (!context) throw new Error("User must be logged in");
  return {
    ActorId: context.currentUser!.id,
    ActorType: context.currentUser!.object_type as UserType,
  };
}
